/*
 * Bülowsen – søgning i headeren.
 * Åbnes med søgeknappen, "/" eller Ctrl/Cmd+K. Live-resultater hentes fra Shopifys predictive search
 * (sektionen bw-predictive-search). Piletaster flytter mellem resultaterne, Enter åbner søgesiden.
 */
(() => {
  if (customElements.get('bw-search')) return;

  const track = (name, data) => window.bwTrack?.(name, data);
  const isTyping = (el) =>
    el instanceof HTMLElement && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName));

  class BwSearch extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.input = this.querySelector('[data-search-input]');
      this.results = this.querySelector('[data-search-results]');
      this.empty = this.querySelector('[data-search-empty]');
      this.loading = this.querySelector('[data-search-loading]');
      this.clearBtn = this.querySelector('[data-search-clear]');
      this.status = this.querySelector('[data-search-live]');
      if (!this.dialog || !this.input) return;

      this.cache = new Map();
      this.controller = new AbortController();
      const { signal } = this.controller;

      document.addEventListener(
        'click',
        (event) => {
          const opener = event.target instanceof Element ? event.target.closest('[data-search-open]') : null;
          if (!opener) return;
          event.preventDefault();
          this.opener = opener;
          this.open();
        },
        { signal }
      );

      document.addEventListener(
        'keydown',
        (event) => {
          if (this.dialog.open) return;
          const k = event.key.toLowerCase();
          if ((k === 'k' && (event.metaKey || event.ctrlKey)) || (event.key === '/' && !isTyping(event.target))) {
            event.preventDefault();
            this.opener = document.activeElement;
            this.open();
          }
        },
        { signal }
      );

      this.querySelector('[data-search-close]')?.addEventListener('click', () => this.close(), { signal });
      this.dialog.addEventListener(
        'click',
        (event) => {
          if (event.target === this.dialog) this.close();
        },
        { signal }
      );
      this.dialog.addEventListener('close', () => this.onClose(), { signal });

      this.input.addEventListener('input', () => this.onInput(), { signal });
      this.dialog.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
      this.clearBtn?.addEventListener(
        'click',
        () => {
          this.input.value = '';
          this.onInput();
          this.input.focus();
        },
        { signal }
      );

      this.querySelectorAll('[data-search-term]').forEach((chip) =>
        chip.addEventListener(
          'click',
          (event) => {
            event.preventDefault();
            this.input.value = chip.dataset.searchTerm;
            this.onInput(true);
            this.input.focus();
            track('soeg_forslag', { term: chip.dataset.searchTerm });
          },
          { signal }
        )
      );

      this.querySelector('form')?.addEventListener(
        'submit',
        (event) => {
          if (!this.input.value.trim()) {
            event.preventDefault();
            return;
          }
          track('soeg', { term: this.input.value.trim() });
        },
        { signal }
      );
    }

    open() {
      if (this.dialog.open) return;
      document.querySelectorAll('dialog[open]').forEach((other) => other !== this.dialog && other.close?.());
      this.dialog.showModal();
      document.documentElement.classList.add('bw-scroll-lock');
      document.querySelectorAll('[data-search-open]').forEach((b) => b.setAttribute('aria-expanded', 'true'));
      requestAnimationFrame(() => {
        this.input.focus();
        this.input.select();
      });
      track('soeg_aabnet');
    }

    close() {
      if (this.dialog.open) this.dialog.close();
    }

    onClose() {
      document.documentElement.classList.remove('bw-scroll-lock');
      document.querySelectorAll('[data-search-open]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
      this.request?.abort();
      if (this.opener instanceof HTMLElement && this.opener.isConnected) this.opener.focus({ preventScroll: true });
    }

    onInput(immediate = false) {
      const q = this.input.value.trim();
      if (this.clearBtn) this.clearBtn.hidden = q.length === 0;
      clearTimeout(this.timer);
      if (!q) {
        this.request?.abort();
        this.setLoading(false);
        this.results.innerHTML = '';
        this.results.hidden = true;
        this.empty.hidden = false;
        this.announce('');
        return;
      }
      this.timer = setTimeout(() => this.fetchResults(q), immediate ? 0 : 160);
    }

    async fetchResults(q) {
      const key = q.toLowerCase();
      if (this.cache.has(key)) {
        this.render(this.cache.get(key));
        return;
      }
      this.request?.abort();
      this.request = new AbortController();
      this.setLoading(true);
      const params = new URLSearchParams({
        q,
        section_id: 'bw-predictive-search',
        'resources[type]': 'product,page,article,collection,query',
        'resources[limit]': '6',
        'resources[limit_scope]': 'each',
        'resources[options][unavailable_products]': 'last',
        'resources[options][fields]': 'title,product_type,variants.title,vendor,tag,body',
      });
      try {
        const response = await fetch(`${this.dataset.suggestUrl}?${params}`, { signal: this.request.signal });
        if (!response.ok) throw new Error(String(response.status));
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const block = doc.querySelector('[data-search-response]');
        const markup = block ? block.innerHTML : '';
        this.cache.set(key, markup);
        if (this.input.value.trim().toLowerCase() === key) this.render(markup);
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.render(
          `<div class="bw-ps__none"><p class="bw-ps__none-title">Søgningen svarer ikke lige nu.</p><p class="bw-ps__none-text">Tryk Enter for at søge på hele siden.</p></div>`
        );
      } finally {
        this.setLoading(false);
      }
    }

    render(markup) {
      this.results.innerHTML = markup;
      this.results.hidden = false;
      this.empty.hidden = true;
      const status = this.results.querySelector('[data-search-status]');
      this.announce(status ? status.textContent : '');
    }

    announce(text) {
      if (this.status) this.status.textContent = text;
    }

    setLoading(on) {
      if (this.loading) this.loading.hidden = !on;
      this.toggleAttribute('data-loading', on);
    }

    items() {
      const scope = this.results.hidden ? this.empty : this.results;
      return Array.from(scope.querySelectorAll('[data-search-item], [data-search-term]')).filter(
        (el) => el.offsetParent !== null
      );
    }

    onKeydown(event) {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      const items = this.items();
      if (!items.length) return;
      event.preventDefault();
      const index = items.indexOf(document.activeElement);
      if (event.key === 'ArrowDown') {
        items[index < 0 ? 0 : Math.min(index + 1, items.length - 1)].focus();
      } else if (index <= 0) {
        this.input.focus();
      } else {
        items[index - 1].focus();
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.request?.abort();
      clearTimeout(this.timer);
    }
  }

  customElements.define('bw-search', BwSearch);
})();
